import * as angular from 'angular';
import * as d3 from 'd3';
import * as $ from 'jquery';
import * as _ from 'lodash';
import * as numeral from 'numeral';
import swivel from 'swivel';

import { tokenFromVizID } from '@mode/charts/domain';
import { EmbedActionType } from '@mode/reports/sandbox/contract';
import { isBuilderMode, StringHelpers } from '@mode/shared/util-js';
import { PIVOT_LIMIT } from '../../../../common/constants';
import { date as dateFormatter } from '../../../../lib/axel/format';
import { ChartController } from './chart-controller';
import template from './chart.html';

function isValidData(data: any) {
  return data !== null && data !== undefined && (typeof data !== 'string' || data.length > 0);
}

// NOTE: taken from http://www.sitepoint.com/javascript-generate-lighter-darker-color/
export function colorLuminance(hex, lum) {
  // validate hex string
  hex = String(hex).replace(/[^0-9a-f]/gi, '');
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;

  // convert to decimal and change luminosity
  let rgb = '#',
    c,
    i;
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
    rgb += ('00' + c).substr(c.length);
  }

  return rgb;
}

export const LegacyChartDirective: angular.IDirectiveFactory = function ($window, $timeout, ChartThemeService) {
  const defaultColors = ChartThemeService.getTheme(ChartThemeService.DEFAULT_THEME_ID).colors;
  const simpleDefaultTheme = ChartThemeService.DEFAULT_SIMPLE_THEME_ID;
  const simpleDefaultColors = ChartThemeService.getTheme(simpleDefaultTheme).colors;

  const MAX_AXIS_LABEL_LENGTH = 175;

  // Will be set later, a bit hacky
  let nv: any;

  function pivotDataset(dataset, groupCol, pivotCol, valueFn, valueCol: string | Array<string>, alias = null) {
    const config = {
      fields: [{ type: 'group', name: groupCol }],
      aggregates: [],
    };
    if (!angular.isArray(valueCol)) {
      valueCol = [<string>valueCol];
    }
    angular.forEach(valueCol, function (col) {
      const aggregate = {
        type: valueFn,
        field: col,
        as: undefined,
      };
      if (alias) {
        aggregate.as = col + alias;
      }
      config.aggregates.push(aggregate);
    });

    if (pivotCol) {
      config.fields.push({ type: 'pivot', name: pivotCol });
    }
    const pivot = swivel(dataset).config(config).all();

    let seriesNames = [];
    if (pivotCol) {
      if (dataset[0] && typeof dataset[0][pivotCol] === 'number') {
        seriesNames = pivot.values(pivotCol).sort(function (a, b) {
          const x = parseFloat(a),
            y = parseFloat(b);
          if (x < y) {
            return -1;
          } else if (x > y) {
            return 1;
          } else {
            return 0;
          }
        });
      } else {
        seriesNames = pivot.values(pivotCol).sort();
      }

      // Build Content
      const content = [];
      _.forEach(pivot.data, function (dataRow) {
        const row = {};

        row[groupCol] = dataRow[groupCol];
        _.forEach(seriesNames, function (seriesName) {
          row[seriesName] = dataRow[seriesName] == null ? null : dataRow[seriesName];
        });

        content.push(row);
      });

      return content;
    } else {
      return pivot.data;
    }
  }

  const buildChart = function (elem, data, chart, scope) {
    customizeTicks(chart);

    subscribeRenderListener(chart, scope.selectedType(), scope.ctrl.trackRenderComplete);

    if (elem.find('svg')[0]) {
      d3.select(elem[0]).select('.chart-svg').html(''); //.select('svg').remove();
    }
    d3.select(elem[0]).select('.chart-svg').append('svg').attr('class', 'nvd3svg').datum(data).call(chart);

    // Escape all tooltip formatters because they inject raw html into the page
    const oldValueFormatter = chart.tooltip.valueFormatter();
    const oldKeyFormatter = chart.tooltip.keyFormatter();
    const oldHeaderFormatter = chart.tooltip.headerFormatter();

    chart.tooltip
      .valueFormatter(function (d, i) {
        return StringHelpers.escapeHTML(oldValueFormatter(d, i));
      })
      .keyFormatter(function (d, i) {
        return StringHelpers.escapeHTML(oldKeyFormatter(d, i));
      })
      .headerFormatter(function (d) {
        return StringHelpers.escapeHTML(oldHeaderFormatter(d));
      });

    let handler;
    if (chart.update) {
      // fixes linePlusBar display bug
      chart.update();
      handler = nv.utils.windowResize(chart.update);
    }

    scope.setChart(chart, handler);
    return chart;
  };

  function subscribeRenderListener(chart, type: string, listener) {
    if (chart.dispatch.hasOwnProperty('renderEnd')) {
      chart.dispatch.on('renderEnd', listener);
    } else {
      nv.dispatch.on('render_end', listener);
    }
  }

  function customizeTicks(chart) {
    if (chart.yAxis && chart.yAxis.ticks() !== null && chart.yAxis.ticks() < 3) {
      chart.yAxis.ticks(3);
    }
  }

  function shouldFormat(num) {
    return Math.abs(num) > 9999999 || (Math.abs(num) < 0.0001 && num !== 0);
  }

  function truncateStringAxes(row) {
    // saftey check so we dont render text labels that are huge
    if (typeof row.x === 'string' && row.x.length > MAX_AXIS_LABEL_LENGTH) {
      row.x = row.x.substring(0, MAX_AXIS_LABEL_LENGTH);
    }
    if (typeof row.label === 'string' && row.label.length > MAX_AXIS_LABEL_LENGTH) {
      row.label = row.label.substring(0, MAX_AXIS_LABEL_LENGTH);
    }
    return row;
  }

  function formatChart(chart, defaults, scale, indexedData: any = undefined) {
    chart.margin({ right: 40, left: 60 });

    chart.xAxis && chart.xAxis.showMaxMin(false);
    chart.yAxis && chart.yAxis.showMaxMin(false);
    chart.y1Axis && chart.y1Axis.showMaxMin(false);
    chart.y2Axis && chart.y2Axis.showMaxMin(false);

    // increasing tick padding by 5px and offsetting by the
    // default 3px
    chart.xAxis && chart.xAxis.tickPadding(8);
    chart.yAxis && chart.yAxis.tickPadding(8);

    if (defaults.xlabel) {
      chart.margin({ bottom: 70 });
    }
    if (defaults.ylabel) {
      chart.margin({ left: 90 });
    }

    function xStringTooLong(text) {
      return text.length > 20;
    }

    function setFontSize() {
      chart.xAxis.fontSize('8px');
    }

    /**
     * Offset date value by user's timezone.  Note that this
     * is destructive and usually for rendering UTC timestamps
     * since this actually changes the point in time value
     * which is fine if you just want to print the date with
     * no timezone but not good if you want to make further
     * transformations
     * @example
     *    let d = new Date('2016-07-01 01:00:00Z');
     *    // -> Thu Jun 30 2016 18:00:00 GMT-0700 (PDT)
     *    offsetDate(d)
     *    // -> Fri Jul 01 2016 01:00:00 GMT-0700 (PDT)
     * @param d
     * @returns {Date}
     */
    function offsetDate(d) {
      const date = new Date(d);
      const userTimezoneOffset = date.getTimezoneOffset() * 6e4;
      return new Date(date.getTime() + userTimezoneOffset);
    }

    if (defaults.xangle) {
      chart.xAxis.rotateLabels(defaults.xangle);
      chart.margin({ bottom: 90 });

      // if x axis is not a date, check for x string length.
      // case for defaults.xformat == 'date' handled later.
      if (indexedData && indexedData.values && defaults.xformat !== 'date') {
        if (
          _.some(indexedData.values, function (val) {
            return val['x'] && xStringTooLong(val['x'].toString());
          })
        ) {
          setFontSize();
        }
      }
    }

    if (defaults.hideLegend) {
      chart.showLegend && chart.showLegend(false);
    }

    // add <null> to null column labels
    if (chart.legend) {
      chart.legend.options().key = function (d) {
        return d.key === undefined || d.key === null ? '<null>' : d.key;
      };
    }

    // if user sets ymin or ymax
    if (angular.isDefined(defaults.ymin) || angular.isDefined(defaults.ymax)) {
      let ymin,
        ymax,
        dataMin = chart.stacked && angular.isDefined(defaults.yminTotal) ? defaults.yminTotal : defaults.yminFromData,
        dataMax = chart.stacked && angular.isDefined(defaults.ymaxTotal) ? defaults.ymaxTotal : defaults.ymaxFromData;

      // only clip edges if there is data outside the ymin and ymax settings
      // e.g. don't clip edges if ymin or ymax was set due to excel range feature and not as a user setting
      if (defaults.ymin > defaults.yminFromData || defaults.ymax < defaults.ymaxFromData) {
        chart.clipEdge && chart.clipEdge(true);
        chart.bars && chart.bars.clipEdge(true);
        chart.lines && chart.lines.clipEdge(true);
      } else {
        // Always clip bars on linePlusBar charts
        chart.clipEdge && chart.clipEdge(false);
        chart.lines && chart.lines.clipEdge(false);
      }

      // for stacked charts, default to the total of all y values
      // otherwise, default to the min/max from the data or zero
      if (isNaN(defaults.ymin) || defaults.ymin == null) {
        ymin = dataMin;
        // if ymin is positive, reset to 0
        ymin = ymin > 0 ? 0 : ymin;
      } else {
        ymin = defaults.ymin;
      }

      if (isNaN(defaults.ymax) || defaults.ymax == null) {
        ymax = dataMax;
      } else {
        ymax = defaults.ymax;
      }

      if (angular.isDefined(defaults.ymin)) {
        chart.forceY && chart.forceY([ymin]);
        chart.bars && chart.bars.forceY([ymin]);
        chart.lines && chart.lines.forceY([ymin]);
      }

      if (angular.isDefined(defaults.ymax)) {
        chart.forceY && chart.forceY([ymax]);
        chart.bars && chart.bars.forceY([ymax]);
        chart.lines && chart.lines.forceY([ymax]);
      }

      if (angular.isDefined(defaults.ymin) && angular.isDefined(defaults.ymax)) {
        chart.forceY && chart.forceY([ymin, ymax]);
        chart.bars && chart.bars.forceY([ymin, ymax]);
        chart.lines && chart.lines.forceY([ymin, ymax]);
      }

      // If ymin setting is more than the minimum data,
      // or ymax setting is less than the maximum data,
      // use yDomain so the range is enforced.
      if (ymin > dataMin || ymax < dataMax) {
        chart.yDomain && chart.yDomain([ymin, ymax]);
        chart.bars && chart.bars.yDomain([ymin, ymax]);
        chart.lines && chart.lines.yDomain([ymin, ymax]);
      }
    } else {
      chart.yNice && chart.yNice(true);
      chart.lines && chart.lines.yNice && chart.lines.yNice(true);
      // disable clipping line charts when default y scale is applied
      chart.lines && chart.lines.clipEdge && chart.lines.clipEdge(false);
    }

    // if user sets xmin or xmax
    if (angular.isDefined(defaults.xmin) || angular.isDefined(defaults.xmax)) {
      let xmin, xmax;

      xmin = isNaN(defaults.xmin) ? defaults.xminFromData : defaults.xmin;
      xmax = isNaN(defaults.xmax) ? defaults.xmaxFromData : defaults.xmax;
      chart.xDomain([xmin, xmax]);
    }

    if (defaults.yformat) {
      if (defaults.yformat !== 'none') {
        const formatter = customYFormat.bind(this, defaults.yformat, defaults.yprecision);
        chart.yAxis && chart.yAxis.tickFormat(formatter);
        chart.y1Axis && chart.y1Axis.tickFormat(formatter);
        chart.y2Axis && chart.y2Axis.tickFormat(formatter);
        chart.valueFormat && chart.valueFormat(formatter);
      }
    } else {
      const autoFormatter = autoFormat.bind(this, scale, defaults.yprecision);
      chart.yAxis && chart.yAxis.tickFormat(autoFormatter);
      chart.y1Axis && chart.y1Axis.tickFormat(autoFormatter);
      chart.y2Axis && chart.y2Axis.tickFormat(autoFormatter);
      chart.valueFormat && chart.valueFormat(autoFormatter);
    }
    // strings shouldn't be formatted
    if (defaults && defaults.xtype === 'string') {
      return;
    }
    if (defaults && isDateFormat(defaults.xformat)) {
      // better format date tick marks in line/scatter xAxis using d3 time scale
      chart.lines && chart.lines.xScale(d3.time.scale());
      chart.bars && chart.lines && chart.bars.xScale(d3.time.scale()); // line + bar chart
      chart.lines && chart.lines.scatter && chart.lines.scatter.xScale(d3.time.scale());
      chart.stacked && chart.stacked.scatter && chart.stacked.scatter.xScale(d3.time.scale());
      chart.scatter && chart.scatter.xScale(d3.time.scale());
      (chart.multibar || chart.discretebar) && chart.reduceXTicks(true); // Reduce ticks on multibar, since it doesn't support linear scales

      // auto
      if (defaults.xformat === 'date') {
        const tickMultiFormat = d3.time.format.multi([
          [
            '%-I:%M%p',
            function (d) {
              return d.getMinutes();
            },
          ], // not the beginning of the hour
          [
            '%-I%p',
            function (d) {
              return d.getHours();
            },
          ], // not midnight
          [
            '%b %-d',
            function (d) {
              return d.getDate() !== 1;
            },
          ], // not the first of the month
          [
            '%b %-d',
            function (d) {
              return d.getMonth();
            },
          ], // not Jan 1st
          [
            '%Y',
            function () {
              return true;
            },
          ],
        ]);

        // scatter plots (and linePlusBarChart) have stateful plot points. any attempt
        // to alter the x or y value will cause it to increment
        // the values on hover.
        // @see https://github.com/novus/nvd3/blob/v1.8.2/src/models/scatter.js#L444
        chart
          .x(function (d) {
            return chart.scatter || (chart.bars && chart.lines) ? new Date(d.x) : offsetDate(d.x);
          })
          .xAxis.tickFormat(function (d) {
            const formattedTick = tickMultiFormat(new Date(d));

            if (defaults.xangle && xStringTooLong(formattedTick)) {
              setFontSize();
            }
            return formattedTick;
          });

        // set up the tooltip to display full dates
        if (chart.interactiveLayer || chart.tooltip) {
          const tooltip = (chart.interactiveLayer && chart.interactiveLayer.tooltip) || chart.tooltip;
          // ignore timezone unless scatter or linePlusBarChart
          const formatOpts = chart.scatter || (chart.bars && chart.lines) ? {} : { ignoreTimezone: true };
          const tsFormat = dateFormatter('mmm d, yyyy hh:mma', formatOpts);
          tooltip.headerFormatter(function (d) {
            if (d) {
              return tsFormat(d);
            }
          });
        }
      } else {
        const dateFormat = {
          date: '%x',
          hour: '%x %X',
          full_mon: '%b %Y',
          full_day: '%e %b %Y',
          usa_full: '%b %e %Y',
          mon_day: '%m/%d',
          year: '%Y',
        };

        // X-axis needs to be pruned if it's determined to be linear data. Otherwise it may crash.
        chart
          .x(function (d) {
            return new Date(d.x);
          })
          .xAxis.tickFormat(function (d) {
            return d3.time.format.utc(dateFormat[defaults.xformat] || '%x')(new Date(d));
          });
      }
    } else if (defaults.xformat) {
      if (defaults.xformat !== 'none') {
        const xformatter = customXFormat.bind(this, indexedData, defaults.xformat, defaults.xprecision);
        chart.xAxis.tickFormat(xformatter);
      }
    } else {
      chart.xAxis.tickFormat(function (d, i, seriesIndex) {
        /**
         * The series index is defined when this method is called in context
         * of it's surrounding ticks i.e. when plotting bars.  Otherwise the
         * tick selection is not in context i.e. when generating a tooltip
         * on hover of a single bar
         * @type {boolean}
         */
        const selectionInContext = angular.isDefined(seriesIndex);

        if (selectionInContext && indexedData) {
          // we want to use the x label instead of the index, which d
          // is set to by d3 when this selection is in context
          d = angular.isDefined(indexedData.values[d]) ? indexedData.values[d].x : void 0;
        }

        // dont display tool tip heading if not defined
        // Fixes - https://app.asana.com/0/0/100085106408576
        if (typeof d === 'undefined') {
          return;
        }

        if (shouldFormat(d)) {
          if (!angular.isNumber(d)) {
            return d;
          }
          const num = d3.formatPrefix(d);
          const roundFunc = angular.isNumber(defaults.xprecision) ? 'toFixed' : 'toPrecision';
          const round = angular.isNumber(defaults.xprecision)
            ? Math.max(1, Math.min(defaults.xprecision, 21))
            : Math.min(5, getPrecisionOfNumber(d));
          return num.scale(d)[roundFunc](round) + num.symbol;
        } else if (angular.isNumber(defaults.xprecision)) {
          return numeral(d).format('0,0' + getPrecisionString(defaults.xprecision));
        } else {
          return numeral(d).format('0,0[.][0000]');
        }
      });

      switch (defaults.type) {
        case 'area':
        case 'line':
          // if the chart has a header enabled, it'll probably format it poorly because we set the x axis values
          // to be the position in the dataset. Default tooltip will use this value so instead, attempt to output
          // the formatted x data
          if (chart.tooltip.headerEnabled()) {
            chart.tooltip.headerFormatter(function (d, i) {
              return chart.xAxis.tickFormat()(indexedData.values[d].x, i);
            });
          }

          if (chart.interactiveLayer && chart.interactiveLayer.tooltip.headerEnabled()) {
            chart.interactiveLayer.tooltip.headerFormatter(function (d, i) {
              return chart.xAxis.tickFormat()(indexedData.values[d].x, i);
            });
          }
          break;
      }
    }
  }

  /**
   * Returns the whole precision of the given number.
   * e.g. 9.99 = 3, 123.321 = 6
   * @param a the number to check
   * @returns {number} integer with the precision
   */
  function getPrecisionOfNumber(a) {
    if (!isFinite(a)) {
      return 0;
    }
    let e = 1,
      p = 0;
    // Count all the digits right of the decimal place
    while (Math.round(a * e) / e !== a) {
      e *= 10;
      p++;
    }
    e = 1;
    // Count all the digits left of the decimal place
    while (Math.floor(a / e) !== 0) {
      e *= 10;
      p++;
    }
    return p;
  }

  function getPrecisionString(count) {
    let result;
    for (let i = 0; i < count; i++) {
      result = !result ? '.0' : result + '0';
    }
    return result;
  }

  function customAxisFormat(d, format, precision) {
    let scaledData;
    if (format === 'percent') {
      scaledData = d * 100;
    } else {
      const scales = { thousands: 1000, millions: 1000000, billions: 1000000000 };
      scaledData = scales[format] ? d / scales[format] : d;
    }
    scaledData = angular.isNumber(precision)
      ? numeral(scaledData).format('0,0' + getPrecisionString(precision))
      : numeral(scaledData).format('0,0[.][0000]');

    switch (format) {
      case 'percent':
        return scaledData + '%';
      case 'currency':
        return '$' + scaledData;
      default:
        return scaledData;
    }
  }

  function customXFormat(indexedData, xformat, xprecision, d) {
    if (indexedData) {
      if (angular.isUndefined(indexedData.values[d])) {
        return undefined;
      }
      d = indexedData.values[d].x;
    }
    if (d) {
      return customAxisFormat(d, xformat, xprecision);
    }
  }

  function customYFormat(yformat, yprecision, d) {
    if (d) {
      return customAxisFormat(d, yformat, yprecision);
    }
  }

  function autoFormat(scale, yprecision, d) {
    if (shouldFormat(d)) {
      if (!angular.isNumber(d)) {
        return '';
      }
      const num = d3.formatPrefix(scale);
      const roundFunc = angular.isNumber(yprecision) ? 'toFixed' : 'toPrecision';
      const round = angular.isNumber(yprecision)
        ? Math.max(1, Math.min(yprecision, 21))
        : Math.min(5, getPrecisionOfNumber(d));
      return numeral(num.scale(d)[roundFunc](round)) + num.symbol;
    } else if (angular.isNumber(yprecision)) {
      return numeral(d).format('0,0' + getPrecisionString(yprecision));
    } else {
      return numeral(d).format('0,0[.][0000]');
    }
  }

  function tooManyRows(data) {
    return data.length > PIVOT_LIMIT;
  }

  function showTooManyRowsError(scope) {
    scope.status.unavailable = true;
    scope.status.hasTooManyRows = true;
    scope.status.message =
      'Mode can plot up to 2,000 distinct values. To create a chart, try selecting an aggregate or pick a different x-axis.';
  }

  /*
   * fill out a color space with the colors supplied for the given size of elements
   * by darkening the color space 20% by each successive step of the total colors given
   */
  function fillColorsToColumns(colors, size) {
    if (size > colors.length) {
      const newColors = [].concat(colors);
      let step = 1;
      for (let i = colors.length; i < size; i += colors.length) {
        for (let j = 0; j < colors.length && j + i < size; j++) {
          // grab the same color index but in the previous grouping and reduce luminance by 20%
          newColors.push(colorLuminance(newColors[(step - 1) * colors.length + j], -0.2));
        }
        step++;
      }
      return newColors;
    } else {
      return colors;
    }
  }

  const renderBarChart = function (elem, dataset, defaults, scope) {
    const label = defaults && defaults.x ? defaults.x : '';
    let ynames =
      defaults && angular.isArray(defaults.y)
        ? defaults.y
        : angular.isDefined(defaults.y)
          ? [defaults.y]
          : [defaults.x];
    const fnames =
      defaults && angular.isArray(defaults.y)
        ? defaults.y
        : angular.isDefined(defaults.y)
          ? [defaults.y]
          : [defaults.x];
    if (defaults.g && ynames && ynames.length && ynames.length < 2) {
      ynames = defaults && angular.isArray(defaults.f) ? defaults.f : [defaults.f];
    }
    const data = [];

    angular.forEach(ynames, function (yname, index) {
      data.push({ values: [], key: yname });
    });

    let scale;
    let chartData = dataset;

    if (angular.isDefined(defaults.g) && defaults.g !== null && fnames.length && fnames.length < 2) {
      chartData = pivotDataset(dataset, defaults.x, defaults.g, defaults.gValueFn, fnames[0]);
      ynames = _.reject(ynames, function (yname: string) {
        return chartData[0] && chartData[0][yname] === undefined;
      });
    } else if (defaults.aggregate && fnames.length) {
      var alias = '_aggregate_of';
      chartData = pivotDataset(dataset, defaults.x, undefined, defaults.aggregate, fnames, alias);
      if (chartData && chartData[0] && isNaN(chartData[0][fnames[0] + alias])) {
        scope.chartError = true;
        return;
      }
    }

    // if after pivot, there are still too many rows
    if (ynames.length && tooManyRows(chartData)) {
      showTooManyRowsError(scope);
      return;
    }

    angular.forEach(chartData, function (row) {
      angular.forEach(ynames, function (yname, index) {
        const yaxisLabel = alias ? yname + alias : yname;
        if (ynames.length > 1 || (typeof row[yaxisLabel] === 'number' && !isNaN(row[yaxisLabel]))) {
          const yaxis =
            typeof row[yaxisLabel] === 'number' && !isNaN(row[yaxisLabel])
              ? row[yaxisLabel]
              : defaults.nullToZero
                ? 0
                : null;
          scale = !angular.isDefined(scale) ? yaxis : Math.max(scale, yaxis);
          if (isValidData(row[label])) {
            // Skip data points with invalid X
            data[index].values.push(
              truncateStringAxes({
                x: row[label],
                y: yaxis,
              })
            );
          }
        }
      });
    });

    nv.addGraph(function () {
      const chart = ynames.length > 1 ? nv.models.multiBarChart() : nv.models.discreteBarChart();

      const getX = function getX(d, i) {
        /**
         * if the selection is in context the caller is calling
         * with the context of the surrounding
         * values i.e. when plotting bars on the x-axis. if it
         * isn't defined that means the opposite i.e. when
         * rendering a tooltip over a specific bar
         * @type {boolean}
         */
        const selectionInContext = angular.isDefined(i);

        if (selectionInContext) {
          return i;
        } else {
          return d.x;
        }
      };

      function curriedXFormatter(xdata) {
        return function formatX(d, i, seriesIndex) {
          /**
           * This tick selection is being called in context
           * of it's surrounding ticks i.e. when plotting bars.
           * Otherwise the tick selection is not in context i.e.
           * when generating a tooltip on hover of a single bar
           * @type {boolean}
           */
          const selectionInContext = angular.isDefined(seriesIndex);

          if (selectionInContext) {
            return angular.isDefined(xdata[0].values[d]) ? xdata[0].values[d].x : void 0;
          } else {
            return d;
          }
        };
      }

      chart.staggerLabels(false).duration(0);

      if (ynames.length <= 1 && !defaults.xhideval) {
        chart.showValues && chart.showValues(true);
      }
      if (data[0] && data[0].values.length > 75) {
        if (ynames.length > 1) {
          chart.reduceXTicks(true);
        }
        chart.showValues && chart.showValues(false);
      } else {
        if (ynames.length > 1) {
          chart.reduceXTicks(false);
        }
      }

      if (!defaults.g || fnames.length > 1) {
        chart.x(getX).xAxis.tickFormat(curriedXFormatter(data));
        // need to format xaxis based on index instead of raw data
        formatChart(chart, defaults, scale, data[0]);
      } else {
        formatChart(chart, defaults, scale);
      }

      if (defaults.stacked && chart.stacked) {
        chart.stacked(true);
      }
      chart.showControls && chart.showControls(false);

      if (defaults.xangle) {
        chart.staggerLabels(false);
      }

      const colors = defaults.colors || defaultColors;
      if (ynames.length > 1) {
        chart.color(fillColorsToColumns(colors, ynames.length));
        //chart.delay(0);
      } else {
        chart.color([colors[0]]);
      }

      try {
        return buildChart(elem, data, chart, scope);
      } catch (e) {
        console.error(e);
      }
    });
  };

  const renderLineChart = function (elem, dataset, defaults, scope) {
    const xname = defaults && defaults.x ? defaults.x : '';
    let ynames =
      defaults && angular.isArray(defaults.y)
        ? defaults.y
        : angular.isDefined(defaults.y)
          ? [defaults.y]
          : [defaults.x];
    const fnames =
      defaults && angular.isArray(defaults.y)
        ? defaults.y
        : angular.isDefined(defaults.y)
          ? [defaults.y]
          : [defaults.x];
    if (defaults.g && ynames.length && ynames.length < 2) {
      ynames = defaults && angular.isArray(defaults.f) ? defaults.f : [defaults.f];
    }
    const label = defaults && defaults.label ? defaults.label : '';
    let data = [];

    angular.forEach(ynames, function (yname, index) {
      data.push({ values: [], key: yname });
    });

    let scale;
    let chartData = dataset;

    if (angular.isDefined(defaults.g) && defaults.g !== null && fnames.length && fnames.length < 2) {
      chartData = pivotDataset(dataset, defaults.x, defaults.g, defaults.gValueFn, fnames[0]);
      ynames = _.reject(ynames, function (yname: string) {
        return chartData[0] && chartData[0][yname] === undefined;
      });
    } else if (defaults.aggregate && fnames.length) {
      var alias = '_aggregate_of';
      chartData = pivotDataset(dataset, defaults.x, undefined, defaults.aggregate, fnames, alias);
      if (chartData && chartData[0] && isNaN(chartData[0][fnames[0] + alias])) {
        scope.chartError = true;
        return;
      }
    }

    // if after pivot, there are still too many rows
    if (ynames.length && tooManyRows(chartData)) {
      showTooManyRowsError(scope);
      return;
    }

    let ymin = Number.MAX_VALUE; //max int
    let ymax = Number.MIN_VALUE;
    angular.forEach(chartData, function (row, rowIndex) {
      angular.forEach(ynames, function (yname, index) {
        const yaxisLabel = alias ? yname + alias : yname;
        const yaxis =
          typeof row[yaxisLabel] === 'number' && !isNaN(row[yaxisLabel])
            ? row[yaxisLabel]
            : defaults.nullToZero
              ? 0
              : null;
        scale = !angular.isDefined(scale) ? yaxis : Math.max(scale, yaxis);
        if (angular.isNumber(yaxis)) {
          ymin = Math.min(ymin, yaxis);
          ymax = Math.max(ymax, yaxis);
        }
        if (isValidData(row[xname])) {
          // Skip data points with invalid X
          data[index].values.push(
            truncateStringAxes({
              x: row[xname],
              y: yaxis,
              index: rowIndex, // Injecting index since getX doesn't reliably return i
              label: row[label],
            })
          );
        }
      });
    });

    const invalidLineChart = _.every(data, function (d) {
      return d.values && d.values.length === 1;
    });
    if (invalidLineChart) {
      data = [];
    }
    // this is to duplicate a feature of excel line charts.
    // if the difference between the max and min is greater than 20%,
    // then you can show the y axis from 0 which looks nicer,
    // otherwise you zoom the yaxis to just around the min and max
    // of the line chart to better show the changes.
    if ((ymin < 0 && ymax < 0) || (ymin > 0 && ymax > 0)) {
      if (!angular.isDefined(defaults.ymin) && ymin > 0 && (ymax - ymin) / ymax > 0.2) {
        defaults.ymin = 0;
      } else if (!angular.isDefined(defaults.ymax) && ymax < 0 && (ymin - ymax) / ymin > 0.2) {
        defaults.ymax = 0;
      }
    }

    nv.addGraph(function () {
      const lineChart = nv.models
        .lineChart()
        .color(fillColorsToColumns(defaults.colors || defaultColors, ynames.length));

      lineChart
        .x(function (d, i) {
          return d.index;
        }) // Using d.index that's injected above
        .xAxis.tickFormat(function (d) {
          const pattern = { index: d };
          const target = _.find(data[0].values, pattern);

          return angular.isDefined(target) ? target['x'] : void 0;
        });

      if (data.length) {
        formatChart(lineChart, defaults, scale, data[0]);
      }
      lineChart.useVoronoi(false);
      lineChart.interactiveLayer.showGuideLine(false);

      lineChart.useInteractiveGuideline(true);

      try {
        return buildChart(elem, data, lineChart, scope);
      } catch (e) {
        console.error(e);
      }
    });
  };

  const renderAreaChart = function (elem, dataset, defaults, scope) {
    const xname = defaults && defaults.x ? defaults.x : '';
    let ynames =
      defaults && angular.isArray(defaults.y)
        ? defaults.y
        : angular.isDefined(defaults.y)
          ? [defaults.y]
          : [defaults.x];
    const fnames =
      defaults && angular.isArray(defaults.y)
        ? defaults.y
        : angular.isDefined(defaults.y)
          ? [defaults.y]
          : [defaults.x];
    if (defaults.g && ynames.length && ynames.length < 2) {
      ynames = defaults && angular.isArray(defaults.f) ? defaults.f : [defaults.f];
    }
    const label = defaults && defaults.label ? defaults.label : '';
    let data = [];

    angular.forEach(ynames, function (yname, index) {
      data.push({ values: [], key: yname });
    });

    let scale;
    let chartData = dataset;

    if (angular.isDefined(defaults.g) && defaults.g !== null && fnames.length && fnames.length < 2) {
      chartData = pivotDataset(dataset, defaults.x, defaults.g, defaults.gValueFn, fnames[0]);
      ynames = _.reject(ynames, function (yname: string) {
        return chartData[0] && chartData[0][yname] === undefined;
      });
    } else if (defaults.aggregate && fnames.length) {
      var alias = '_aggregate_of';
      chartData = pivotDataset(dataset, defaults.x, undefined, defaults.aggregate, fnames, alias);
      if (chartData && chartData[0] && isNaN(chartData[0][fnames[0] + alias])) {
        scope.chartError = true;
        return;
      }
    }

    // if after pivot, there are still too many rows
    if (ynames.length && tooManyRows(chartData)) {
      showTooManyRowsError(scope);
      return;
    }

    angular.forEach(chartData, function (row) {
      angular.forEach(ynames, function (yname, index) {
        const yaxisLabel = alias ? yname + alias : yname;
        if (ynames.length > 1 || (typeof row[yaxisLabel] === 'number' && !isNaN(row[yaxisLabel]))) {
          const yaxis = typeof row[yaxisLabel] === 'number' && !isNaN(row[yaxisLabel]) ? row[yaxisLabel] : 0;
          scale = !angular.isDefined(scale) ? yaxis : Math.max(scale, yaxis);
          if (isValidData(row[xname])) {
            // Skip data points with invalid X
            data[index].values.push(
              truncateStringAxes({
                x: row[xname],
                y: yaxis,
                label: row[label],
              })
            );
          }
        }
      });
    });

    const invalidLineChart = _.every(data, function (d) {
      return d.values && d.values.length === 1;
    });
    if (invalidLineChart) {
      data = [];
    }

    nv.addGraph(function () {
      const chart = nv.models.stackedAreaChart();
      chart.color(fillColorsToColumns(defaults.colors || defaultColors, ynames.length));

      chart
        .x(function (d) {
          return d.index;
        })
        .xAxis.tickFormat(function (d) {
          return angular.isDefined(data[0].values[d]) ? data[0].values[d].x : undefined;
        });

      chart.showControls(false);

      if (defaults.expanded && chart.style) {
        chart.style('expand');
      }

      if (data.length) {
        formatChart(chart, defaults, scale, data[0]);
      }
      chart.useVoronoi(false);
      chart.interactiveLayer.showGuideLine(false);
      chart.useInteractiveGuideline(true);

      try {
        return buildChart(elem, data, chart, scope);
      } catch (e) {
        console.error(e);
      }
    });
  };

  function isDateFormat(format) {
    // Don't include format none here as we don't want to do any date formatting
    return (
      format === 'date' ||
      format === 'hour' ||
      format === 'full_mon' ||
      format === 'full_day' ||
      format === 'usa_full' ||
      format === 'mon_day' ||
      format === 'year'
    );
  }

  const renderScatterChart = function (elem, dataset, defaults, scope) {
    const xname = defaults && defaults.x ? defaults.x : '';
    let ynames = defaults && angular.isArray(defaults.y) ? defaults.y : [defaults.y];
    const size = defaults && defaults.size ? defaults.size : undefined;
    if (defaults.g) {
      ynames = defaults && angular.isArray(defaults.f) ? defaults.f : [defaults.f];
    }
    const label = defaults && defaults.label ? defaults.label : '';
    const data = [];

    angular.forEach(ynames, function (yname, index) {
      data.push({ values: [], key: yname });
    });

    let scale;
    angular.forEach(dataset, function (row) {
      angular.forEach(ynames, function (yname, index) {
        scale = !angular.isDefined(scale) ? row[yname] : Math.max(scale, row[yname]);

        if (isValidData(row[xname])) {
          // Skip data points with invalid X
          data[index].values.push(
            truncateStringAxes({
              x: row[xname],
              y: row[yname],
              label: row[label],
              size: size ? row[size] : 1,
            })
          );
        }
      });
    });

    nv.addGraph(function () {
      const chart = nv.models
        .scatterChart()
        .color(fillColorsToColumns(defaults.colors || defaultColors, ynames.length));

      if (ynames.length <= 1) {
        chart.showLegend(false);
      }

      chart.clipVoronoi(false);

      if (chart.interactiveLayer || chart.tooltip) {
        /**
         * Decorated Content Generator
         *
         * Calls the original tool tip content generator
         * before prepending the label heading to the tooltips
         * raw HTML
         * @param {function} superContentGen
         * @returns {string}
         */
        const decoratedContentGenerator = function decoratedContentGenerator(superContentGen) {
          return function decorator(d) {
            const html = superContentGen.apply(void 0, [d]);
            const heading = (str) => `<h3>${str}</h3>`;
            const formatOpts = chart.scatter || (chart.bars && chart.lines) ? {} : { ignoreTimezone: true };
            const tooltipLabel = (str) => {
              if (typeof str === 'string' && Date.parse(str) > 0) {
                return dateFormatter('MMM d, yyyy hh:mmA', formatOpts)(new Date(str));
              } else {
                return str;
              }
            };

            return [heading(tooltipLabel(d.point.label)), html].join('');
          };
        };

        const tooltip = (chart.interactiveLayer && chart.interactiveLayer.tooltip) || chart.tooltip;

        // set contentGenerator with new decorated contentGenerator
        // after retrieving a reference to the original contentGenerator
        tooltip.contentGenerator(decoratedContentGenerator(tooltip.contentGenerator()));
      }

      formatChart(chart, defaults, scale);

      try {
        return buildChart(elem, data, chart, scope);
      } catch (e) {
        console.error(e);
      }
    });
  };

  const renderPieChart = function (elem, dataset, defaults, scope) {
    const value = defaults && defaults.value ? defaults.value : '';
    const label = defaults && angular.isArray(defaults.label) ? defaults.label : [defaults.label];

    const data = [];
    let chartData = dataset;

    if (defaults.aggregate && label) {
      var alias = '_aggregate_of';
      chartData = pivotDataset(dataset, label, undefined, defaults.aggregate, [value], alias);
      if (chartData && chartData[0] && isNaN(chartData[0][value + alias])) {
        scope.chartError = true;
        return;
      }
    }

    // if after pivot, there are still too many rows
    if (tooManyRows(chartData)) {
      showTooManyRowsError(scope);
      return;
    }

    angular.forEach(chartData, function (row) {
      const aliasValue = alias ? value + alias : value;
      if (typeof row[aliasValue] === 'number' && !isNaN(row[aliasValue])) {
        if (isValidData(row[aliasValue])) {
          // Skip data points with invalid X
          data.push(
            truncateStringAxes({
              value: row[aliasValue],
              label: row[label],
            })
          );
        }
      }
    });

    if (data.length > 30) {
      scope.chartMessage = 'The legend is hidden for pie charts with over 30 slices';
    }

    nv.addGraph(function () {
      const pieChart = nv.models
        .pieChart()
        .x(function (d) {
          return d.label;
        })
        .y(function (d) {
          return d.value;
        })
        .showLabels(true)
        .labelThreshold(0.05)
        .labelsOutside(true)
        .color(fillColorsToColumns(defaults.colors || defaultColors, data.length));

      if (data.length > 30 || defaults.hideLegend) {
        pieChart.showLegend(false);
      }

      if (defaults.xhideval) {
        pieChart.showLabels(false);
      }
      if (defaults.donut) {
        pieChart.donut(true).donutRatio(0.35);
      }
      if (!defaults.realvalue) {
        pieChart.labelType('percent');
      }

      try {
        return buildChart(elem, data, pieChart, scope);
      } catch (e) {
        console.error(e);
      }
    });
  };

  const renderLinePlusBarChart = function (elem, dataset, defaults, scope) {
    let xname = defaults && defaults.x ? defaults.x : '',
      ynames,
      fnames;

    if (defaults.ybars && defaults.ylines) {
      ynames = fnames = [defaults.ybars, defaults.ylines];
    } else {
      ynames = fnames =
        defaults && angular.isArray(defaults.y)
          ? defaults.y
          : angular.isDefined(defaults.y)
            ? [defaults.y]
            : [defaults.x];
    }

    const label = defaults && defaults.label ? defaults.label : '';
    let data = [];

    angular.forEach(ynames, function (yname, index) {
      data.push({ values: [], key: yname });
    });

    let scale;
    let chartData = dataset;

    if (defaults.aggregate && fnames.length) {
      var alias = '_aggregate_of';
      chartData = pivotDataset(dataset, defaults.x, undefined, defaults.aggregate, fnames, alias);
      if (chartData && chartData[0] && isNaN(chartData[0][fnames[0] + alias])) {
        scope.chartError = true;
        return;
      }
    }

    // if after pivot, there are still too many rows
    if (ynames.length && tooManyRows(chartData)) {
      showTooManyRowsError(scope);
      return;
    }

    let ymin = Number.MAX_VALUE, //max int
      ymax = Number.MIN_VALUE,
      colors,
      barsData;

    // if current theme is in simpleThemes, keep it.
    // otherwise, set to default simple colors.
    if (defaults.colors && _.includes(scope.simpleThemes, scope.view.theme)) {
      colors = defaults.colors;
    } else {
      colors = simpleDefaultColors;
      scope.setTheme(simpleDefaultTheme);
    }

    angular.forEach(chartData, function (row) {
      angular.forEach(ynames, function (yname, index) {
        const yaxisLabel = alias ? yname + alias : yname;
        const yaxis =
          typeof row[yaxisLabel] === 'number' && !isNaN(row[yaxisLabel])
            ? row[yaxisLabel]
            : defaults.nullToZero
              ? 0
              : null;
        scale = !angular.isDefined(scale) ? yaxis : Math.max(scale, yaxis);
        if (angular.isNumber(yaxis)) {
          ymin = Math.min(ymin, yaxis);
          ymax = Math.max(ymax, yaxis);
        }
        if (defaults.ybars && data[index].key === defaults.ybars) {
          barsData = data[index];
        }
        if (isValidData(row[xname])) {
          // Skip data points with invalid X
          data[index].values.push(
            truncateStringAxes({
              x: row[xname],
              y: yaxis,
              label: row[label],
            })
          );
        }
      });
    });

    if (!defaults.ybars && data[0]) {
      barsData = data[0];
    }

    if (barsData) {
      barsData.bar = true;
      barsData.color = colors[4];
    }

    const invalidLinePlusBarChart = _.every(data, function (d) {
      return d.values && d.values.length === 1;
    });
    if (invalidLinePlusBarChart) {
      data = [];
    }
    // this is to duplicate a feature of excel line charts.
    // if the difference between the max and min is greater than 20%,
    // then you can show the y axis from 0 which looks nicer,
    // otherwise you zoom the yaxis to just around the min and max
    // of the line chart to better show the changes.
    if ((ymin < 0 && ymax < 0) || (ymin > 0 && ymax > 0)) {
      if (!angular.isDefined(defaults.ymin) && ymin > 0 && (ymax - ymin) / ymax > 0.2) {
        defaults.ymin = 0;
      } else if (!angular.isDefined(defaults.ymax) && ymin < 0 && (ymin - ymax) / ymin > 0.2) {
        defaults.ymax = 0;
      }
    }

    nv.addGraph(function () {
      const linePlusBarChart = nv.models.linePlusBarChart().color(colors),
        isDate = defaults.xformat && isDateFormat(defaults.xformat);

      const getX = function getX(d, i) {
        /**
         * if the selection is in context the caller is calling
         * with the context of the surrounding
         * values i.e. when plotting bars on the x-axis. if it
         * isn't defined that means the opposite i.e. when
         * rendering a tooltip over a specific bar
         * @type {boolean}
         */
        const selectionInContext = angular.isDefined(i);

        if (selectionInContext) {
          return i;
        } else {
          return d.x;
        }
      };

      const curriedXFormatter = function curriedXFormatter(xdata) {
        return function formatX(d, i, seriesIndex) {
          /**
           * This tick selection is being called in context
           * of it's surrounding ticks i.e. when plotting bars.
           * Otherwise the tick selection is not in context i.e.
           * when generating a tooltip on hover of a single bar
           * @type {boolean}
           */
          const selectionInContext = angular.isDefined(seriesIndex);
          let dx;

          if (selectionInContext) {
            dx = angular.isDefined(xdata[0].values[d]) ? xdata[0].values[d].x : void 0;
            return isDate ? d3.time.format.utc('%x')(new Date(dx)) : dx;
          } else {
            return d;
          }
        };
      };

      /**
       * Omit Undefined Key Formatter
       *
       * Decorate keyformatter to omit undefined values
       * @param chart
       * @returns {function(any, any): *}
       * @see https://app.asana.com/0/0/134958593515437
       */
      const omitUndefinedKeyFormatter = function omitUndefinedKeyFormatter(chart) {
        const formatter = chart.tooltip.keyFormatter();
        const resolveKey = (k) => ('undefined' === typeof k ? '' : k);

        return function applyFormatterToSafeKey(d, i) {
          return formatter.call(null, resolveKey(d), i);
        };
      };

      // LinePlusBar charts like to animate
      linePlusBarChart.lines.duration(0);
      linePlusBarChart.lines2.duration(0);
      linePlusBarChart.y1Axis.duration(0);
      linePlusBarChart.y2Axis.duration(0);
      linePlusBarChart.y3Axis.duration(0);
      linePlusBarChart.y4Axis.duration(0);
      linePlusBarChart.xAxis.duration(0);
      linePlusBarChart.x2Axis.duration(0);

      linePlusBarChart.focusEnable(false);

      linePlusBarChart.x(getX).xAxis.tickFormat(curriedXFormatter(data));

      /**
       * Hiding undefined key is a compromise for tooltips who have undefined
       * datum values in the keyFormatter callback
       * @todo we still need to solve for undefined key/label
       * @see https://app.asana.com/0/0/134958593515437
       */
      linePlusBarChart.tooltip.keyFormatter(omitUndefinedKeyFormatter(linePlusBarChart));

      if (data.length) {
        formatChart(linePlusBarChart, defaults, scale, data[0]);

        // Hack because NVD3 1.8.5 has a different bottom margin for linePlusBar.
        if (!defaults.xangle && !defaults.xlabel) {
          linePlusBarChart.margin({ bottom: 50 });
        }
      }
      linePlusBarChart.useVoronoi(true);
      try {
        return buildChart(elem, data, linePlusBarChart, scope);
      } catch (e) {
        console.error(e);
      }
    });
  };

  return {
    template: template,

    controller: ChartController,
    controllerAs: 'ctrl',
    restrict: 'AE',
    scope: {
      dataset: '=',
      view: '=?setView',
      status: '=?',
      chartExists: '=',
      chartId: '@id',
      removeFn: '&',
      saveFn: '&',
      cancelFn: '&',
      onAction: '&',
      onEdit: '&?',
      onRenderStart: '&',
      onRenderEnd: '&',
      viewer: '<',
      redrawOnDataChange: '@?',
    },

    link: function (scope: any, elem, attrs) {
      scope.chartReference;
      scope.chartHandler;
      scope.token = tokenFromVizID(scope.chartId);
      scope.isBuilderMode = isBuilderMode($window.document.body);

      const $elem = $(elem);

      scope.copySuccess = function () {
        scope.onAction({
          $action: {
            type: EmbedActionType.ShowAlert,
            success: true,
            message: 'Link to chart copied successfully',
          },
        });
      };

      scope.setChart = function (chart, handler) {
        if (scope.chartHandler) {
          scope.chartHandler.clear();
        }

        scope.chartHandler = handler;
        scope.chartReference = chart;
      };

      scope.render = function (dataset, type, defaults) {
        scope.chartMessage = '';
        scope.chartError = false;

        if ($(elem).find('svg')[0]) {
          $elem.find('svg').first().remove();
        }

        import('nvd3').then((nvd3) => {
          nv = nvd3;
          $timeout(function () {
            switch (type) {
              case 'bar':
                renderBarChart($elem, dataset, angular.extend({ colors: scope.view.chartColors }, defaults), scope);
                break;
              case 'line':
                renderLineChart($elem, dataset, angular.extend({ colors: scope.view.chartColors }, defaults), scope);
                break;
              case 'linePlusBar':
                renderLinePlusBarChart(
                  $elem,
                  dataset,
                  angular.extend({ colors: scope.view.chartColors }, defaults),
                  scope
                );
                break;
              case 'area':
                renderAreaChart($elem, dataset, angular.extend({ colors: scope.view.chartColors }, defaults), scope);
                break;
              case 'scatter':
                renderScatterChart($elem, dataset, angular.extend({ colors: scope.view.chartColors }, defaults), scope);
                break;
              case 'pie':
                renderPieChart($elem, dataset, angular.extend({ colors: scope.view.chartColors }, defaults), scope);
                break;
              default:
                renderBarChart($elem, dataset, null, scope);
            }
          }, 100);
        });
      };

      scope.$on('$destroy', function () {
        // handle tooltip cleanup either on chart or chart.interactiveLayer
        if (scope.chartReference) {
          if (
            scope.chartReference.interactiveLayer &&
            scope.chartReference.interactiveLayer.tooltip &&
            typeof scope.chartReference.interactiveLayer.tooltip.id === 'function'
          ) {
            $(`#${scope.chartReference.interactiveLayer.tooltip.id()}`).remove();
          }
          if (scope.chartReference.tooltip && typeof scope.chartReference.tooltip.id === 'function') {
            $(`#${scope.chartReference.tooltip.id()}`).remove();
          }
        }

        if ($elem.find('svg')[0]) {
          d3.select($elem[0]).select('.chart-svg').select('svg').remove();
        }
        scope.setChart();
      });
    },
  };
};

LegacyChartDirective.$inject = ['$window', '$timeout', 'ChartThemeService'];
